import { useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  type RouteObject,
} from "react-router-dom";

import { useAuth } from "../shared/auth/auth-context.js";
import { SettingsPage } from "../features/settings/settings-page.js";
import { BotDetailPage } from "../features/bots/bot-detail-page.js";
import { BotEditorPage } from "../features/bots/bot-form.js";
import { BotListPage } from "../features/bots/bot-list-page.js";
import { BacktestDetailPage } from "../features/bots/backtest-detail-page.js";
import { PositionsPage } from "../features/positions/positions-page.js";
import { ActivitiesPage } from "../features/activities/activities-page.js";
import { TradesPage } from "../features/trades/trades-page.js";
import { TickerDetailPage } from "../features/tickers/ticker-detail-page.js";
import { TickersPage } from "../features/tickers/tickers-page.js";
import { AdminPage } from "../features/admin/admin-page.js";
import { BacktestsPage } from "../features/backtests/backtests-page.js";
import { AiAnalysisPage } from "../features/ai-analysis/ai-analysis-page.js";
import { Button } from "../shared/ui/button.js";
import { ResourceState } from "../shared/ui/resource-state.js";
import { AppShell } from "./app-shell.js";

function SignInPage() {
  const auth = useAuth();
  const [search] = useSearchParams();
  const returnTo = search.get("returnTo") ?? "/positions";
  return (
    <main className="centered-layout">
      <section className="state-panel" aria-labelledby="sign-in-title">
        <p className="eyebrow">Secure stock bot operations</p>
        <h1 id="sign-in-title">Sign in to TradingApp</h1>
        <p>
          Use your existing AutoTradingTech identity. Broker secrets never enter browser storage.
        </p>
        {auth.error === null ? null : (
          <p className="form-message form-message--error">{auth.error}</p>
        )}
        <Button onClick={() => void auth.signIn(returnTo)}>Continue with AutoTradingTech</Button>
      </section>
    </main>
  );
}

function CallbackPage() {
  const { completeSignIn } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState("Verifying your secure session…");
  useEffect(() => {
    let active = true;
    completeSignIn(window.location.href)
      .then((returnTo) => {
        if (active) void navigate(returnTo, { replace: true });
      })
      .catch(() => {
        if (active) setMessage("Sign-in could not be completed. Return to sign in and try again.");
      });
    return () => {
      active = false;
    };
  }, [completeSignIn, navigate]);
  return (
    <main className="centered-layout">
      <ResourceState kind="loading" title="Completing sign in" description={message} />
    </main>
  );
}

function SignedOutPage() {
  return (
    <main className="centered-layout">
      <section className="state-panel" aria-labelledby="signed-out-title">
        <p className="eyebrow">Session closed</p>
        <h1 id="signed-out-title">You are signed out</h1>
        <p>No broker credentials or provider tokens were retained by TradingApp.</p>
        <Button onClick={() => window.location.assign("/sign-in")}>Sign in again</Button>
      </section>
    </main>
  );
}

function ProtectedLayout() {
  const auth = useAuth();
  const location = useLocation();
  if (auth.status === "bootstrapping") {
    return (
      <main className="centered-layout">
        <ResourceState
          kind="loading"
          title="Restoring your session"
          description="TradingApp is checking your secure identity and workspace."
        />
      </main>
    );
  }
  if (auth.status !== "authenticated" || auth.principal === null) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate replace to={`/sign-in?returnTo=${returnTo}`} />;
  }
  return (
    <AppShell
      isAdmin={auth.principal.isAdmin}
      onSignOut={auth.signOut}
      {...(auth.principal.email === undefined ? {} : { email: auth.principal.email })}
    />
  );
}

function AdminGuard() {
  const auth = useAuth();
  return auth.principal?.isAdmin === true ? (
    <Outlet />
  ) : (
    <ResourceState
      kind="forbidden"
      title="Administrator access required"
      description="TradingEngine did not authorize this identity for operational status."
    />
  );
}

function BotDetailRoute() {
  const { botId = "" } = useParams();
  return <BotDetailPage botId={botId} />;
}

function BotEditorRoute() {
  const { botId } = useParams();
  return <BotEditorPage {...(botId === undefined ? {} : { botId })} />;
}

function BacktestDetailRoute() {
  const { backtestId = "", botId = "" } = useParams();
  return <BacktestDetailPage backtestId={backtestId} botId={botId} />;
}

function TickerDetailRoute() {
  const { symbol = "" } = useParams();
  return <TickerDetailPage key={symbol.toUpperCase()} symbol={symbol.toUpperCase()} />;
}

function LegacyActivitiesRedirect() {
  const location = useLocation();
  return <Navigate replace to={{ pathname: "/history", search: location.search }} />;
}

export const appRoutes: readonly RouteObject[] = [
  { path: "/sign-in", element: <SignInPage /> },
  { path: "/auth/callback", element: <CallbackPage /> },
  { path: "/signed-out", element: <SignedOutPage /> },
  {
    element: <ProtectedLayout />,
    children: [
      { index: true, element: <Navigate replace to="/positions" /> },
      {
        path: "/positions",
        element: <PositionsPage />,
      },
      {
        path: "/trades",
        element: <TradesPage />,
      },
      {
        path: "/history",
        element: <ActivitiesPage />,
      },
      { path: "/activities", element: <LegacyActivitiesRedirect /> },
      { path: "/bots", element: <BotListPage /> },
      { path: "/bots/new", element: <BotEditorRoute /> },
      { path: "/bots/:botId", element: <BotDetailRoute /> },
      { path: "/bots/:botId/edit", element: <BotEditorRoute /> },
      { path: "/bots/:botId/backtests/:backtestId", element: <BacktestDetailRoute /> },
      { path: "/backtests", element: <BacktestsPage /> },
      { path: "/tickers", element: <TickersPage /> },
      { path: "/tickers/:symbol", element: <TickerDetailRoute /> },
      { path: "/ai-analysis", element: <AiAnalysisPage /> },
      {
        path: "/settings",
        element: <SettingsPage />,
      },
      {
        element: <AdminGuard />,
        children: [
          {
            path: "/admin",
            element: <AdminPage />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: (
      <main className="centered-layout">
        <ResourceState
          kind="not-found"
          title="Page not found"
          description="This TradingApp route does not exist."
          action={
            <Button onClick={() => window.location.assign("/positions")}>Go to Positions</Button>
          }
        />
      </main>
    ),
  },
];
